import {
  BgMotivStyled,
  ColumnStyled,
  DatePickersGroupStyled,
  DatePickerStyled,
  FilterGroupStyled,
  RowFilterHeadlineStyled,
  RowFilterStyled,
  RowLoadingStyled,
  RowPaginationStyled,
  RowSortStyled,
} from './OverviewEvents.styles';
import { Breadcrumb } from 'components/molecules/Breadcrumb';
import { BreakpointContext } from 'contexts/breakpoint/BreakpointContext';
import { cleanRouterQuery, mapObjectToQueryString } from 'helpers/url';
import { CtaDropdown } from 'components/molecules/CtaDropdown';
import { EventSliderItemProps } from 'components/molecules/EventSliderItem/EventSliderItem.types';
import { EventsOverviewContentHeader } from './EventsOverviewContentHeader.partial';
import { formatDateForQuery } from 'helpers/date';
import { isAfter } from 'date-fns';
import { Loading } from 'components/atoms/Loading';
import {
  mapEdgeItemToEventBoxItem,
  mapMultipleItems,
} from 'components/molecules/EventSliderItem/EventSliderItem.helper';
import { OverviewEventsProps } from './OverviewEvents.types';
import { ResultsCounterStyled, SpacerStyled } from 'components/molecules/FilterRows';
import { Separator } from 'components/organisms/Separator';
import { SortGroupStyled } from 'components/molecules/FilterRows';
import { useContext, useEffect, useRef, useState } from 'react';
import { useLinkResolver } from 'prismic/helpers/useLinkResolver';
import ComponentsRenderer from 'components/templates/partials/ComponentsRenderer';
import Container from 'components/layout/Grid/Container';
import get from 'lodash/get';
import Image from 'next/image';
import Router, { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import Link from 'components/atoms/Link/Link';
import EventsList from './EventsList';

export const OverviewEvents: React.FC<OverviewEventsProps> = ({ events, data }) => {
  const router = useRouter();
  const bp = useContext(BreakpointContext);
  const { t, lang } = useTranslation('common');
  const { locale } = useLinkResolver();
  const [isloading, setIsLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState('asc');

  const isMobile = bp === 'xs' || bp === 'sm';
  const gridItems = 6;

  let _items: EventSliderItemProps[] = [];

  if (events && events.items) {
    // @ts-ignore
    _items = events.items.map((item) => {
      if (item && item.node && item.node.body && item.node.body.length === 1) {
        return mapEdgeItemToEventBoxItem(locale, item);
      }
    });

    events.items.map((item) => {
      if (item && item.node && item.node.body && item.node.body.length > 1) {
        item.node.body?.map(() => {
          const MultiEvents = mapMultipleItems(locale, item);
          _items.push.apply(_items, MultiEvents);
        });
      }
    });
  }

  let Events: EventSliderItemProps[] = [];
  Events = _items
    .filter((element) => element !== undefined)
    .filter(
      (value, index, self) =>
        index === self.findIndex((obj) => JSON.stringify(obj) === JSON.stringify(value))
    )
    .sort((a, b) => Number(a._dateToSort) - Number(b._dateToSort));

  const mapStartDate = (): Date | undefined => {
    const queryObj = cleanRouterQuery(router.query);
    const queryDate = queryObj.startDate;
    const singleQueryDate = Array.isArray(queryDate) ? queryDate[0] : queryDate;
    if (singleQueryDate) {
      return queryDate ? new Date(singleQueryDate) : new Date();
    }
    return new Date();
  };

  const mapEndDate = (): Date | undefined => {
    const queryObj = cleanRouterQuery(router.query);
    const queryDate = queryObj.endDate;
    const singleQueryDate = Array.isArray(queryDate) ? queryDate[0] : queryDate;
    if (singleQueryDate) {
      return queryDate ? new Date(singleQueryDate) : new Date();
    }
    return new Date();
  };

  const [cursor, setCursor] = useState<number>(gridItems);
  const [startDate, setStartDate] = useState(mapStartDate());
  const [endDate, setEndDate] = useState(mapEndDate());

  // Change url with dates in query  on change datepickers
  const updateQueryDates = (): void => {
    if (startDate && endDate) {
      const queryObj = cleanRouterQuery(router.query);
      queryObj.startDate = formatDateForQuery(startDate);
      queryObj.endDate = formatDateForQuery(endDate);
      const queryString = mapObjectToQueryString(queryObj);
      setCursor(gridItems);
      router
        .push(`/${lang}${router.asPath.split('?')[0]}${queryString}`, undefined)
        .catch((err) => {
          console.error('Navigationsfehler:', err);
        });
    }
  };

  // Avoid double call by dates States on initial Load
  function useDidUpdateEffect(fn: any, inputs: any): void {
    const didMountRef = useRef(false);

    useEffect(() => {
      if (didMountRef.current) {
        return fn();
      }
      didMountRef.current = true;
    }, inputs);
  }

  useDidUpdateEffect(updateQueryDates, [startDate, endDate]);

  const onEndDateChange = (date: Date | null) => {
    if (!date) {
      return;
    }
    setEndDate(date);
  };

  const onStartDateChange = (date: Date | null) => {
    if (!date) {
      return;
    }

    if (date && endDate && isAfter(date, endDate)) {
      setEndDate(date);
    }
    setStartDate(date);
  };

  const getLoadMoreLink = () => {
    setCursor((prevState) => prevState + gridItems);
  };

  useEffect(() => {
    const onRouteChangeError = (err: any, url: any) => {
      console.error(`Fehler beim Navigieren zu ${url}:`, err);
    };
    const start = () => {
      setIsLoading(true);
    };
    const end = () => {
      setIsLoading(false);
    };
    Router.events.on('routeChangeStart', start);
    Router.events.on('routeChangeComplete', end);
    Router.events.on('routeChangeError', onRouteChangeError);
    return () => {
      Router.events.off('routeChangeStart', start);
      Router.events.off('routeChangeComplete', end);
      Router.events.off('routeChangeError', onRouteChangeError);
    };
  }, []);

  const isSortSelected = (): string => {
    const queryObj = cleanRouterQuery(router.query);
    const querySort = queryObj.sort;
    const singleQueryCat = Array.isArray(querySort) ? querySort[0] : querySort;
    return singleQueryCat || '';
  };

  const sortOptions = [
    {
      label: t('sortDateAsc'),
      value: 'asc',
    },
    {
      label: t('sortDateDes'),
      value: 'desc',
    },
  ];

  const onClickSort = (value: string): void => {
    setSortOrder(value);
    const queryObj = cleanRouterQuery(router.query);
    if (value) {
      queryObj.sort = value;
    }
    const queryString = mapObjectToQueryString(queryObj);
    router.push(`/${lang}${router.asPath.split('?')[0]}${queryString}`, undefined);
  };

  const rowNumItemsComponent = (
    <ResultsCounterStyled>
      {Events.length} {t('events')}
    </ResultsCounterStyled>
  );

  const sortSelectionComponent = (
    <SortGroupStyled>
      <span>{t('sortBy')}</span>
      <CtaDropdown
        options={sortOptions}
        defaultValue={isSortSelected()}
        onSelectCallback={(value) => {
          onClickSort(value);
        }}
      />
    </SortGroupStyled>
  );

  return (
    <>
      <Container>
        <ColumnStyled span={12}>
          <BgMotivStyled data-type="motiv">
            <Image
              alt="Tagesplan motiv"
              src="/motivs/tagesplan-motiv.png"
              width={1555}
              height={1962}
            />
          </BgMotivStyled>
          <Breadcrumb />
          <EventsOverviewContentHeader slices={get(data, 'data.body', [])} />
          <RowFilterStyled>
            <FilterGroupStyled>
              <RowFilterHeadlineStyled>{t('date')}</RowFilterHeadlineStyled>
              <DatePickersGroupStyled>
                <DatePickerStyled
                  startDate={startDate}
                  endDate={endDate}
                  currentDate={startDate}
                  onChange={(date) => onStartDateChange(date)}
                />
                <SpacerStyled>{t('FilterTo')}</SpacerStyled>
                <DatePickerStyled
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  currentDate={endDate}
                  onChange={(date) => onEndDateChange(date)}
                />
              </DatePickersGroupStyled>
            </FilterGroupStyled>
          </RowFilterStyled>
          {isMobile ? (
            <RowSortStyled>
              {sortSelectionComponent}
              {rowNumItemsComponent}
            </RowSortStyled>
          ) : (
            <RowSortStyled>
              {rowNumItemsComponent}
              {sortSelectionComponent}
            </RowSortStyled>
          )}
          <EventsList events={Events} sortOrder={sortOrder} cursor={cursor} />
          <RowLoadingStyled>
            {isloading && <Loading />}
            {!isloading && !events?.items && <div>{t('noResults')}</div>}
            {events && events.items && events.items.length === 0 && !isloading && (
              <div>{t('noResults')}</div>
            )}
          </RowLoadingStyled>{' '}
          {cursor < Events.length && (
            <RowPaginationStyled>
              <Link skin="tertiarySmall" onPress={() => getLoadMoreLink()} url={undefined}>
                {t('links.showMore')}
              </Link>
            </RowPaginationStyled>
          )}
        </ColumnStyled>
      </Container>
      <Separator primary={{ horizontal_line: true }} />
      <ComponentsRenderer slices={get(data, 'data.body', [])} />
    </>
  );
};
